'use client';

import { useState, useRef, useEffect } from 'react';
import { useTranslations } from 'next-intl';

interface PriceRangeFilterProps {
  selectedRanges: string[];
  onRangesChange: (ranges: string[]) => void;
  className?: string;
}

export default function PriceRangeFilter({
  selectedRanges,
  onRangesChange,
  className = ""
}: PriceRangeFilterProps) {
  const t = useTranslations('auctions.filters');
  const [isOpen, setIsOpen] = useState(false);
  const [manualMin, setManualMin] = useState('');
  const [manualMax, setManualMax] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState<'left' | 'right'>('left');

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDropdownToggle = () => {
    if (!isOpen) {
      // Dropdown açılırken pozisyonu hesapla - requestAnimationFrame ile forced reflow'u önle
      if (dropdownRef.current) {
        requestAnimationFrame(() => {
          if (dropdownRef.current) {
            const rect = dropdownRef.current.getBoundingClientRect();
            const viewportWidth = window.innerWidth;
            const dropdownWidth = 300; // Tahmini dropdown genişliği
            
            if (rect.left + dropdownWidth > viewportWidth - 20) {
              setDropdownPosition('right');
            } else {
              setDropdownPosition('left');
            }
          }
        });
      }
    }
    setIsOpen(!isOpen);
  };

  const handleManualRangeApply = () => {
    const min = parseInt(manualMin.replace(/[^\d]/g, '')) || 0;
    const max = parseInt(manualMax.replace(/[^\d]/g, '')) || 0;
    
    if (min > 0 && max > 0 && min <= max) {
      const manualRangeId = `manual-${min}-${max}`;
      // Tekli seçim - önceki seçimi kaldır, yenisini ekle
      onRangesChange([manualRangeId]);
      setManualMin('');
      setManualMax('');
      setIsOpen(false); // Dropdown'ı kapat
    }
  };

  const isApplyDisabled = () => {
    const min = parseInt(manualMin.replace(/[^\d]/g, '')) || 0;
    const max = parseInt(manualMax.replace(/[^\d]/g, '')) || 0;
    return !manualMin || !manualMax || min > max;
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const formatInputPrice = (value: string) => {
    // Sadece rakamları al ve binlik ayırıcı ekle
    const numbers = value.replace(/[^\d]/g, '');
    if (numbers === '') return '';
    
    const num = parseInt(numbers);
    return num.toLocaleString('tr-TR');
  };

  const handleManualMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatInputPrice(e.target.value);
    setManualMin(formatted);
  };

  const handleManualMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatInputPrice(e.target.value);
    setManualMax(formatted);
  };

  const getDisplayText = () => {
    if (selectedRanges.length === 0) return t('priceRange');
    const rangeId = selectedRanges[0];
    if (rangeId.startsWith('manual-')) {
      const [min, max] = rangeId.replace('manual-', '').split('-').map(Number);
      return `${formatPrice(min)} - ${formatPrice(max)}`;
    }
    return t('priceRange');
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={handleDropdownToggle}
        className="flex items-center justify-between w-full px-3 sm:px-6 py-2 sm:py-3 bg-white border border-gray-200 rounded-full shadow-sm hover:shadow-md transition-shadow"
      >
        <span className="text-sm sm:text-base font-semibold text-gray-900 truncate">
          {getDisplayText()}
        </span>
        <svg
          className={`w-4 h-4 sm:w-5 sm:h-5 text-gray-600 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className={`absolute top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 w-full min-w-[280px] sm:min-w-[300px] max-w-[350px] sm:max-w-[400px] ${
          dropdownPosition === 'right' ? 'right-0' : 'left-0'
        }`}>
          <div className="p-4 sm:p-6">
            {/* Manuel Fiyat Girişi */}
            <div className="flex items-center gap-2 sm:gap-3 mb-3">
              <div className="flex-1">
                <label className="block text-sm text-gray-700 mb-1">{t('minPrice')}</label>
                <input
                  type="text"
                  value={manualMin}
                  onChange={handleManualMinChange}
                  placeholder="0"
                  className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-md text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>
              <span className="text-gray-600 font-medium text-base">-</span>
              <div className="flex-1">
                <label className="block text-sm text-gray-700 mb-1">{t('maxPrice')}</label>
                <input
                  type="text"
                  value={manualMax}
                  onChange={handleManualMaxChange}
                  placeholder="1.000.000"
                  className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-md text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>
            </div>
            <button
              onClick={handleManualRangeApply}
              disabled={isApplyDisabled()}
              className="w-full py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors text-sm sm:text-base"
            >
              {t('apply')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
